import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, Globe, MapPin, Sun, Moon, Calendar, Info } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов часовых поясов
import WorldClockDisplay from './WorldClockPage/components/WorldClockDisplay';
import SearchLocationForm from './WorldClockPage/components/SearchLocationForm';
import TimezoneConverter from './WorldClockPage/components/TimezoneConverter';
import MeetingPlanner from './WorldClockPage/components/MeetingPlanner';
import PopularCitiesSection from './WorldClockPage/components/PopularCitiesSection';
import TimezoneMap from './WorldClockPage/components/TimezoneMap';
import AnalogClock from './WorldClockPage/components/AnalogClock';
import TimezoneFacts from './WorldClockPage/components/TimezoneFacts';

/**
 * WorldClockPage Component - Страница с мировыми часами и конвертером часовых поясов
 */
const WorldClockPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('timezone-converter'), []);
  const pageTitle = tool?.metaTitle || 'Мировые часы | Часовые пояса | Gensio';
  const metaDescription = tool?.metaDescription || 'Узнайте точное время в любом городе мира, сравните часовые пояса, запланируйте международные встречи и звонки с учетом разницы во времени. Актуальная информация о часовых поясах России и всего мира.';

  // --- Управление состоянием ---
  const [userTimeZone, setUserTimeZone] = useState('');
  const [selectedCities, setSelectedCities] = useState([
    { name: 'Москва', timezone: 'Europe/Moscow', country: 'Россия', flag: '🇷🇺' },
    { name: 'Нью-Йорк', timezone: 'America/New_York', country: 'США', flag: '🇺🇸' },
    { name: 'Лондон', timezone: 'Europe/London', country: 'Великобритания', flag: '🇬🇧' },
    { name: 'Токио', timezone: 'Asia/Tokyo', country: 'Япония', flag: '🇯🇵' }
  ]);
  const [currentTab, setCurrentTab] = useState('world-clock'); // 'world-clock', 'converter', 'meeting-planner'
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // --- Получение локальной временной зоны пользователя ---
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setUserTimeZone(tz);
    } catch (error) {
      console.error('Ошибка при определении часового пояса:', error);
      setUserTimeZone('Europe/Moscow'); // Fallback на Москву
    }
  }, []);

  // --- Обновление часов каждую секунду ---
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Обработка subToolId для предварительной настройки страницы ---
  useEffect(() => {
    if (!subToolId) return;

    switch (subToolId) {
      case 'world-clock':
        setCurrentTab('world-clock');
        break;
      case 'meeting-planner':
        setCurrentTab('meeting-planner');
        break;
      case 'time-difference':
        setCurrentTab('converter');
        break;
      default:
        setCurrentTab('world-clock');
    }
  }, [subToolId]);

  // --- Добавление города в список отслеживаемых ---
  const addCity = (city) => {
    if (!selectedCities.some(c => c.name === city.name)) {
      setSelectedCities([...selectedCities, city]);
    }
  };

  // --- Удаление города из списка отслеживаемых ---
  const removeCity = (cityName) => {
    setSelectedCities(selectedCities.filter(city => city.name !== cityName));
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Globe className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Мировые часы и часовые пояса'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Вкладки для переключения между функциями --- */}
            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-8">
                <button
                  onClick={() => setCurrentTab('world-clock')}
                  className={`px-1 py-4 text-sm font-medium border-b-2 ${
                    currentTab === 'world-clock'
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Clock className="w-5 h-5 inline-block mr-1" />
                  Мировые часы
                </button>
                <button
                  onClick={() => setCurrentTab('converter')}
                  className={`px-1 py-4 text-sm font-medium border-b-2 ${
                    currentTab === 'converter'
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <Calendar className="w-5 h-5 inline-block mr-1" />
                  Конвертер часовых поясов
                </button>
                <button
                  onClick={() => setCurrentTab('meeting-planner')}
                  className={`px-1 py-4 text-sm font-medium border-b-2 ${
                    currentTab === 'meeting-planner'
                      ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <MapPin className="w-5 h-5 inline-block mr-1" />
                  Планировщик встреч
                </button>
              </nav>
            </div>

            {/* --- Основной контент в зависимости от выбранной вкладки --- */}
            {currentTab === 'world-clock' && (
              <>
                <WorldClockDisplay 
                  selectedCities={selectedCities} 
                  userTimeZone={userTimeZone}
                  currentTime={currentTime}
                  onRemoveCity={removeCity}
                />
                <SearchLocationForm onAddCity={addCity} />
                <PopularCitiesSection onSelectCity={addCity} />
                <TimezoneMap />
              </>
            )}

            {currentTab === 'converter' && (
              <TimezoneConverter userTimeZone={userTimeZone} />
            )}

            {currentTab === 'meeting-planner' && (
              <MeetingPlanner selectedCities={selectedCities} onAddCity={addCity} />
            )}

            {/* --- Информационный контент о часовых поясах --- */}
            <TimezoneFacts />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Аналоговые часы текущего пользователя --- */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm flex flex-col items-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 self-start flex items-center">
                <Clock className="w-5 h-5 mr-2 text-indigo-500" />
                Местное время
              </h3>
              <AnalogClock size={180} />
              <div className="mt-4 text-center">
                <div className="text-xl font-bold text-gray-900 dark:text-white">
                  {currentTime.toLocaleTimeString('ru-RU')}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {currentTime.toLocaleDateString('ru-RU', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </div>
                <div className="mt-1 text-xs text-indigo-600 dark:text-indigo-400">
                  {userTimeZone ? userTimeZone.replace('_', ' ') : 'Определение...'}
                </div>
              </div>
            </div>

            {/* --- Информация о дне/ночи в мире --- */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl border border-blue-100 dark:border-blue-800 p-5 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <Sun className="w-5 h-5 mr-2 text-amber-500" />
                <Moon className="w-5 h-5 mr-2 text-indigo-500" />
                День и ночь в мире
              </h3>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  В настоящий момент светло в странах:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-2 rounded border border-amber-100 dark:border-amber-800 flex items-center">
                    <Sun className="w-4 h-4 text-amber-500 mr-2" />
                    <span>Владивосток</span>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-2 rounded border border-amber-100 dark:border-amber-800 flex items-center">
                    <Sun className="w-4 h-4 text-amber-500 mr-2" />
                    <span>Токио</span>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-2 rounded border border-amber-100 dark:border-amber-800 flex items-center">
                    <Sun className="w-4 h-4 text-amber-500 mr-2" />
                    <span>Сидней</span>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-2 rounded border border-amber-100 dark:border-amber-800 flex items-center">
                    <Sun className="w-4 h-4 text-amber-500 mr-2" />
                    <span>Пекин</span>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                  В настоящий момент темно в странах:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded border border-indigo-100 dark:border-indigo-800 flex items-center">
                    <Moon className="w-4 h-4 text-indigo-500 mr-2" />
                    <span>Нью-Йорк</span>
                  </div>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded border border-indigo-100 dark:border-indigo-800 flex items-center">
                    <Moon className="w-4 h-4 text-indigo-500 mr-2" />
                    <span>Лондон</span>
                  </div>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded border border-indigo-100 dark:border-indigo-800 flex items-center">
                    <Moon className="w-4 h-4 text-indigo-500 mr-2" />
                    <span>Париж</span>
                  </div>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded border border-indigo-100 dark:border-indigo-800 flex items-center">
                    <Moon className="w-4 h-4 text-indigo-500 mr-2" />
                    <span>Рио-де-Жанейро</span>
                  </div>
                </div>
              </div>
            </div>

            {/* --- Карта часовых поясов России --- */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-red-500" />
                Часовые пояса России
              </h3>
              
              <div className="space-y-2 mb-4">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <p>В России 11 часовых поясов от UTC+2 до UTC+12:</p>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Калининград (MSK-1)</span>
                  <span className="font-medium">UTC+2</span>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Москва (MSK)</span>
                  <span className="font-medium">UTC+3</span>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Самара (MSK+1)</span>
                  <span className="font-medium">UTC+4</span>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Екатеринбург (MSK+2)</span>
                  <span className="font-medium">UTC+5</span>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Омск (MSK+3)</span>
                  <span className="font-medium">UTC+6</span>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Красноярск (MSK+4)</span>
                  <span className="font-medium">UTC+7</span>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Иркутск (MSK+5)</span>
                  <span className="font-medium">UTC+8</span>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Якутск (MSK+6)</span>
                  <span className="font-medium">UTC+9</span>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Владивосток (MSK+7)</span>
                  <span className="font-medium">UTC+10</span>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Магадан (MSK+8)</span>
                  <span className="font-medium">UTC+11</span>
                </div>
                <div className="p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between">
                  <span>Камчатка (MSK+9)</span>
                  <span className="font-medium">UTC+12</span>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
                <Info className="w-4 h-4 inline-block mr-1" />
                <span>С 2014 года в России отменен сезонный перевод часов. Используется постоянное зимнее время.</span>
              </div>
            </div>

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WorldClockPage;